import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { CardHeader, Col, Row } from "reactstrap";
import ResourceSelect from "../../../../../components/Inputs/ResourceSelect";
import Select from "../../../../../components/Inputs/Select";
import ColumnToggle from "../../../../../components/NTable/Actions/ColumnToggle";
import Filters from "../../../../../components/NTable/Actions/Filters";
import { useColumnFiltersConfig } from "../../../../../components/NTable/hook/useColumnFiltersConfig";
import {
	getPathWithParams,
	getSettingsResourceSearchFilters,
	renderSettingsResourceLabel,
} from "../../../../../utils/helpers/settings";
import { payCodeCalcFields } from "../helper";
import EnabledDisabledSelect from "./EnabledDisabledSelect";

const OVERTIME_FILTERS = [
	{
		name: "weekly-overtime",
		label: "weekly-overtime",
	},
	{
		name: "daily-overtime",
		label: "daily-overtime",
	},
	{
		name: "daily-double-time",
		label: "daily-double-time",
	},
	{
		name: "daily-double-time-and-half",
		label: "daily-double-time-and-half",
	},
	{
		name: "six-consecutive-day",
		label: "6th-consecutive-day",
	},
	{
		name: "consecutive-day",
		label: "7th-consecutive-day",
	},
	{
		name: "all-hours-worked-on",
		label: "all-hours-worked-on",
	},
];

function Actions({ children, filters, setFilters, columns }) {
	const { t } = useTranslation();
	const baseConfig = useColumnFiltersConfig(columns);

	const config = useMemo(() => {
		const filterConfig = [
			...baseConfig,
			{
				name: "regularDefaultPayCode.id",
				label: t("regular-default-pay-code"),
				type: "resource-select",
				component: (
					<ResourceSelect
						labelPropName="description"
						resourcePath={getPathWithParams("/pay-code", true, {}, payCodeCalcFields)}
						renderLabel={renderSettingsResourceLabel}
						valuePropName="code"
						hasSearch
						getSearchFilters={getSettingsResourceSearchFilters}
						mode="multiple"
					/>
				),
			},
			{
				name: "timesheetMethod",
				label: t("timesheet-method"),
				type: "select",
				component: (
					<Select showSearch>
						<Select.Option value="weekly">{t("weekly")}</Select.Option>
						<Select.Option value="team">{t("team")}</Select.Option>
					</Select>
				),
			},
			{
				name: "splitOvernight",
				label: t("split-overnight"),
				type: "select",
				component: <EnabledDisabledSelect />,
			},
			{
				name: "weightedRate",
				label: t("weighted-rate"),
				type: "select",
				component: <EnabledDisabledSelect />,
			},
			{
				name: "overtime",
				label: t("overtime"),
				children: OVERTIME_FILTERS.map(({ name, label }) => ({
					name,
					label: t(label),
					type: "select",
					component: <EnabledDisabledSelect />,
				})),
			},
			{
				name: "dailyGuarantee",
				label: t("daily-guarantee"),
				type: "select",
				component: <EnabledDisabledSelect />,
			},
			{
				name: "breaksType",
				label: t("breaks"),
				type: "select",
				component: (
					<Select showSearch>
						<Select.Option value="manual">{t("manual")}</Select.Option>
						<Select.Option value="automatic">{t("automatic")}</Select.Option>
						<Select.Option value="rest">{t("rest")}</Select.Option>
						<Select.Option value="none">{t("none")}</Select.Option>
					</Select>
				),
			},
			{
				name: "autoClockOut",
				label: t("auto-clock-out"),
				type: "select",
				component: <EnabledDisabledSelect />,
			},
			{
				name: "shiftPremium",
				label: t("shift-premium"),
				type: "select",
				component: (
					<ResourceSelect
						resourcePath="/calc-rules/shift-premium"
						renderLabel={(option) => {
							if (option.label) {
								return option.label;
							}
							return renderSettingsResourceLabel(option);
						}}
						hasSearch
						getSearchFilters={getSettingsResourceSearchFilters}
						valuePropName="id"
						extraOptions={[
							{ id: 'true', label: t("enabled") },
							{ id: 'false', label: t("disabled") },
						]}
					/>
				),
			},
			{
				name: "mealPremium",
				label: t("meal-premium"),
				type: "select",
				component: (
					<ResourceSelect
						resourcePath="/calc-rules/meal-premium"
						renderLabel={(option) => {
							if (option.label) {
								return option.label;
							}
							return renderSettingsResourceLabel(option);
						}}
						hasSearch
						getSearchFilters={getSettingsResourceSearchFilters}
						valuePropName="id"
						extraOptions={[
							{ id: 'true', label: t("enabled") },
							{ id: 'false', label: t("disabled") },
						]}
					/>
				),
			},
			{
				name: "clockException",
				label: t("clock-exceptions"),
				type: "select",
				component: (
					<ResourceSelect
						resourcePath="/calc-rules/clock-exceptions"
						renderLabel={(option) => {
							if (option.label) {
								return option.label;
							}
							return renderSettingsResourceLabel(option);
						}}
						hasSearch
						getSearchFilters={getSettingsResourceSearchFilters}
						valuePropName="id"
						extraOptions={[
							{ id: 'true', label: t("enabled") },
							{ id: 'false', label: t("disabled") },
						]}
					/>
				),
			},
			{
				name: "amountPerUnit",
				label: t("amount-per-unit"),
				type: "select",
				component: (
					<ResourceSelect
						resourcePath="/calc-rules/amount-per-unit"
						renderLabel={(option) => {
							if (option.label) {
								return option.label;
							}
							return renderSettingsResourceLabel(option);
						}}
						hasSearch
						getSearchFilters={getSettingsResourceSearchFilters}
						valuePropName="id"
						extraOptions={[
							{ id: 'true', label: t("enabled") },
							{ id: 'false', label: t("disabled") },
						]}
					/>
				),
			},
			{
				name: "holiday",
				label: t("holiday"),
				type: "select",
				component: <EnabledDisabledSelect />,
			},
			{
				name: "rounding",
				label: t("rounding"),
				type: "select",
				component: (
					<Select showSearch>
						<Select.Option value={true}>{t("enabled")}</Select.Option>
						<Select.Option value={false}>{t("disabled")}</Select.Option>
						<Select.Option value={15}>{t("15-minutes")}</Select.Option>
						<Select.Option value={6}>{t("6-minutes")}</Select.Option>
						<Select.Option value={5}>{t("5-minutes")}</Select.Option>
					</Select>
				),
			},
		];
		return filterConfig;
	}, [t, baseConfig]);

	return (
		<CardHeader className="border-top p-3">
			<Row className="d-flex align-items-center justify-content-between mb-3 mb-md-0">
				<Col md={12} lg={4}>{children}</Col>
				<Col md={12} lg={children ? 8 : 12}>
					<div className="d-flex align-items-start">
						<Filters
							config={config}
							filters={filters}
							setFilters={setFilters}
						/>
						<ColumnToggle columns={columns} />
					</div>
				</Col>
			</Row>
		</CardHeader>
	);
}

export default Actions;
